import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import customDocumentService from '../../services/api/customDocumentService';
import TemplateEditor from '../customDocuments/TemplateEditor';
import CustomPlaceholderForm from '../customDocuments/CustomPlaceholderForm';
import type {
  DocumentTemplateSummary,
  CustomDocumentConfig,
  CustomPlaceholder,
} from '../../types/customDocuments';
import ConfirmDialog from '../common/ConfirmDialog';
import '../customDocuments/CustomDocuments.css';

type SubTab = 'templates' | 'placeholders';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const CustomDocumentsConfigPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const orgId = user?.organization?.id || '';

  const [subTab, setSubTab] = useState<SubTab>('templates');

  // Templates state
  const [templates, setTemplates] = useState<DocumentTemplateSummary[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  // Placeholders state
  const [config, setConfig] = useState<CustomDocumentConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [editingPlaceholder, setEditingPlaceholder] = useState<CustomPlaceholder | undefined>(undefined);
  const [showPlaceholderForm, setShowPlaceholderForm] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const showConfirm = (title: string, message: string, onConfirm: () => void, confirmLabel?: string, cancelLabel?: string) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm, confirmLabel, cancelLabel });
  };

  const closeConfirm = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  // ---- Data Loading ----

  const loadTemplates = useCallback(async () => {
    if (!orgId) return;
    setTemplatesLoading(true);
    try {
      const data = await customDocumentService.listTemplates(orgId);
      setTemplates(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTemplatesLoading(false);
    }
  }, [orgId]);

  const loadConfig = useCallback(async () => {
    if (!orgId) return;
    setConfigLoading(true);
    try {
      const data = await customDocumentService.getConfig(orgId);
      setConfig(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setConfigLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (subTab === 'templates') loadTemplates();
    if (subTab === 'placeholders') loadConfig();
  }, [subTab, loadTemplates, loadConfig]);

  // ---- Template Actions ----

  const handleDuplicate = async (templateId: string) => {
    try {
      await customDocumentService.duplicateTemplate(orgId, templateId);
      loadTemplates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleTemplateActive = async (templateId: string, isActive: boolean) => {
    try {
      await customDocumentService.setTemplateActive(orgId, templateId, !isActive);
      loadTemplates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    showConfirm(
      t('admin.customDocuments.confirm.deleteTemplate', 'Delete Template'),
      t('admin.customDocuments.confirm.deleteTemplateMessage', 'Are you sure you want to delete this template?'),
      async () => {
        closeConfirm();
        try {
          await customDocumentService.deleteTemplate(orgId, templateId);
          loadTemplates();
        } catch (err: any) {
          setError(err.message);
        }
      },
      t('admin.customDocuments.confirm.delete', 'Delete'),
    );
  };

  // ---- Placeholder Actions ----

  const handleDeletePlaceholder = (placeholderId: string) => {
    showConfirm(
      t('admin.customDocuments.confirm.deletePlaceholder', 'Delete Placeholder'),
      t('admin.customDocuments.confirm.deletePlaceholderMessage', 'Delete placeholder "{{id}}"? Templates using this token will render it as empty.', { id: placeholderId }),
      async () => {
        closeConfirm();
        try {
          await customDocumentService.deletePlaceholder(orgId, placeholderId);
          loadConfig();
        } catch (err: any) {
          setError(err.message);
        }
      },
      t('admin.customDocuments.confirm.delete', 'Delete'),
    );
  };

  // ---- Template Editor overlay ----

  const closeEditorAndNavigate = (tab: SubTab) => {
    showConfirm(
      t('admin.customDocuments.confirm.unsavedChanges', 'Unsaved Changes'),
      t('admin.customDocuments.editor.unsavedChanges', 'You have unsaved changes. Leave anyway?'),
      () => {
        closeConfirm();
        setEditingTemplateId(null);
        setCreatingTemplate(false);
        setSubTab(tab);
      },
      t('admin.customDocuments.confirm.leave', 'Leave'),
      t('admin.customDocuments.confirm.stay', 'Stay'),
    );
  };

  if (creatingTemplate || editingTemplateId) {
    return (
      <div className="custom-docs-config-panel">
        <div className="custom-docs-sub-tabs">
          <button className={`custom-docs-sub-tab ${subTab === 'templates' ? 'active' : ''}`} onClick={() => closeEditorAndNavigate('templates')} type="button">
            {t('admin.customDocuments.subTabs.templates', 'Templates')}
          </button>
          <button className={`custom-docs-sub-tab ${subTab === 'placeholders' ? 'active' : ''}`} onClick={() => closeEditorAndNavigate('placeholders')} type="button">
            {t('admin.customDocuments.subTabs.placeholders', 'Custom Placeholders')}
          </button>
        </div>
        <TemplateEditor
          clientId={orgId}
          templateId={editingTemplateId || undefined}
          onSave={() => { setEditingTemplateId(null); setCreatingTemplate(false); loadTemplates(); }}
          onCancel={() => { setEditingTemplateId(null); setCreatingTemplate(false); }}
        />
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          cancelLabel={confirmDialog.cancelLabel}
          onConfirm={confirmDialog.onConfirm}
          onCancel={closeConfirm}
        />
      </div>
    );
  }

  // ---- Main Panel ----

  return (
    <div className="custom-docs-config-panel">
      <div className="custom-docs-sub-tabs">
        <button
          className={`custom-docs-sub-tab ${subTab === 'templates' ? 'active' : ''}`}
          onClick={() => setSubTab('templates')}
          type="button"
        >
          {t('admin.customDocuments.subTabs.templates', 'Templates')}
        </button>
        <button
          className={`custom-docs-sub-tab ${subTab === 'placeholders' ? 'active' : ''}`}
          onClick={() => setSubTab('placeholders')}
          type="button"
        >
          {t('admin.customDocuments.subTabs.placeholders', 'Custom Placeholders')}
        </button>
      </div>

      {error && (
        <div className="config-error">
          {error}
          <button type="button" onClick={() => setError(null)} className="btn-close-sm">&times;</button>
        </div>
      )}

      {/* ---- Templates Sub-tab ---- */}
      {subTab === 'templates' && (
        <div className="sub-tab-content">
          <div className="sub-tab-header">
            <h4>{t('admin.customDocuments.templates.title', 'Document Templates')}</h4>
            <button type="button" className="btn-primary" onClick={() => setCreatingTemplate(true)}>
              + {t('admin.customDocuments.templates.newTemplate', 'New Template')}
            </button>
          </div>

          {templatesLoading ? (
            <div className="tab-loading"><div className="spinner" /><p>{t('admin.customDocuments.editor.loading', 'Loading...')}</p></div>
          ) : templates.length === 0 ? (
            <p className="empty-state">{t('admin.customDocuments.templates.noTemplates', 'No templates configured. Create your first template.')}</p>
          ) : (
            <table className="template-list-table">
              <thead>
                <tr>
                  <th>{t('admin.customDocuments.templates.columns.name', 'Name')}</th>
                  <th>{t('admin.customDocuments.templates.columns.description', 'Description')}</th>
                  <th>{t('admin.customDocuments.templates.columns.status', 'Status')}</th>
                  <th>{t('admin.customDocuments.templates.columns.updatedAt', 'Updated At')}</th>
                  <th>{t('admin.customDocuments.templates.columns.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((tmpl) => (
                  <tr key={tmpl.templateId} className={`template-row ${!tmpl.isActive ? 'inactive' : ''}`}>
                    <td>{tmpl.name}</td>
                    <td>{tmpl.description || '-'}</td>
                    <td><span className={`status-badge ${tmpl.isActive ? 'active' : 'inactive'}`}>{tmpl.isActive ? t('admin.customDocuments.templates.statusActive', 'Active') : t('admin.customDocuments.templates.statusInactive', 'Inactive')}</span></td>
                    <td>{tmpl.updatedAt ? formatDate(tmpl.updatedAt) : '-'}</td>
                    <td className="actions-cell">
                      <button type="button" className="cd-action-btn" onClick={() => setEditingTemplateId(tmpl.templateId)}>{t('admin.customDocuments.templates.actions.edit', 'Edit')}</button>
                      <button type="button" className="cd-action-btn" onClick={() => handleDuplicate(tmpl.templateId)}>{t('admin.customDocuments.templates.actions.duplicate', 'Duplicate')}</button>
                      <button type="button" className="cd-action-btn" onClick={() => handleToggleTemplateActive(tmpl.templateId, tmpl.isActive)}>
                        {tmpl.isActive ? t('admin.customDocuments.templates.actions.deactivate', 'Deactivate') : t('admin.customDocuments.templates.actions.activate', 'Activate')}
                      </button>
                      <button type="button" className="cd-action-btn cd-danger" onClick={() => handleDeleteTemplate(tmpl.templateId)}>{t('admin.customDocuments.templates.actions.delete', 'Delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ---- Placeholders Sub-tab ---- */}
      {subTab === 'placeholders' && (
        <div className="sub-tab-content">
          <div className="sub-tab-header">
            <h4>{t('admin.customDocuments.placeholders.title', 'Custom Placeholders')}</h4>
            <button type="button" className="btn-primary" onClick={() => { setEditingPlaceholder(undefined); setShowPlaceholderForm(true); }}>
              + {t('admin.customDocuments.placeholders.addPlaceholder', 'Add Placeholder')}
            </button>
          </div>

          {configLoading ? (
            <div className="tab-loading"><div className="spinner" /><p>{t('admin.customDocuments.editor.loading', 'Loading...')}</p></div>
          ) : !config || config.customPlaceholders.length === 0 ? (
            <p className="empty-state">{t('admin.customDocuments.placeholders.noPlaceholders', 'No custom placeholders configured.')}</p>
          ) : (
            <table className="template-list-table">
              <thead>
                <tr>
                  <th>{t('admin.customDocuments.placeholders.columns.label', 'Label')}</th>
                  <th>{t('admin.customDocuments.placeholders.columns.token', 'Token')}</th>
                  <th>{t('admin.customDocuments.placeholders.columns.type', 'Type')}</th>
                  <th>{t('admin.customDocuments.placeholders.columns.value', 'Value / Preview')}</th>
                  <th>{t('admin.customDocuments.placeholders.columns.updatedAt', 'Updated At')}</th>
                  <th>{t('admin.customDocuments.placeholders.columns.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {config.customPlaceholders.map((p) => (
                  <tr key={p.id}>
                    <td>{p.label}</td>
                    <td><code>{p.token}</code></td>
                    <td>{p.type}</td>
                    <td>{p.type === 'IMAGE' ? (p.storagePath ? t('admin.customDocuments.placeholders.imageUploaded', 'Image uploaded') : t('admin.customDocuments.placeholders.noImage', 'No image')) : (p.value || '-')}</td>
                    <td>{p.updatedAt ? formatDate(p.updatedAt) : '-'}</td>
                    <td className="actions-cell">
                      <button type="button" className="cd-action-btn" onClick={() => { setEditingPlaceholder(p); setShowPlaceholderForm(true); }}>{t('admin.customDocuments.placeholders.actions.edit', 'Edit')}</button>
                      <button type="button" className="cd-action-btn cd-danger" onClick={() => handleDeletePlaceholder(p.id)}>{t('admin.customDocuments.placeholders.actions.delete', 'Delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {showPlaceholderForm && (
            <div className="modal-overlay" onClick={() => setShowPlaceholderForm(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <CustomPlaceholderForm
                  clientId={orgId}
                  placeholder={editingPlaceholder}
                  onSave={(updatedConfig) => { setConfig(updatedConfig); setShowPlaceholderForm(false); }}
                  onClose={() => setShowPlaceholderForm(false)}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default CustomDocumentsConfigPanel;
